import React, { useState, useEffect, useRef, useMemo } from 'react';
import '../styles/TableauBord.css';
import { getDashboardStats, getVentesParConseillere, getConsultationsParConseillere, getStatutsLeadsParConseillere, getVueGlobaleDashboard, getMesVentes, getRendezVousAVenir, getRendezVousAujourdhui, getActivitesRecentes, getConsultations, getConsultationStats } from '../services/dashboardAPI';
import { connectSocket } from '../services/socket';
import ClientDetailModal from './ClientDetailModal';
import clientsAPI from '../services/clientsAPI';
import { getAllLeads } from '../services/leadsAPI';
import statisticsService from '../services/statisticsService';
import MesTaches from './MesTaches';
import ConsultationAnalytics, { exportRows as consultationExportRows } from './ConsultationAnalytics';
import { consultationHistoryForName } from '../utils/consultationHistory';
import { useToast } from './notifications/ToastProvider';

const DASHBOARD_RESPONSIBLE_ROLES = ['super_admin', 'administrateur', 'admin', 'manager', 'directeur'];

const EMPTY_VUE_GLOBALE = {
  leads: { total: 0, nouveaux: 0, contactes: 0, rendezVousPris: 0, aTraiter: 0, convertis: 0, perdus: 0, tauxConversion: 0, statuts: [], sources: [] },
  dossiers: { nouveaux: 0, enCours: 0, enAttente: 0, aDeposer: 0, deposes: 0, termines: 0, annules: 0 },
  rendezVous: { total: 0, aVenir: 0, realises: 0, annules: 0, absents: 0, nonRenseignes: 0, tauxPresence: null },
  comparaison: {
    leads: { precedent: 0, evolution: null },
    conversions: { precedent: 0, evolution: null },
    revenus: { precedent: [], evolution: [] }
  },
  relances: {
    total: 0,
    sansActivite3Jours: 0,
    sansActivite7Jours: 0,
    nouveaux: 0,
    aRecontacter: 0,
    injoignables: 0
  },
  facturation: {
    total: 0,
    encaisse: [],
    aEncaisser: [],
    statuts: {
      brouillon: { nombre: 0, montants: [] },
      payable: { nombre: 0, montants: [] },
      partiellement_payee: { nombre: 0, montants: [] },
      payee: { nombre: 0, montants: [] },
      en_retard: { nombre: 0, montants: [] },
      annulee: { nombre: 0, montants: [] }
    }
  }
};

function TableauBord({ userRole = 'directeur', userName = 'Utilisateur Test', onNavigate }) {
  const { showSuccess, showError, showWarning, showInfo } = useToast();
  
  // État pour la période sélectionnée
  const [periode, setPeriode] = useState('jour');
  const [consultationExportData, setConsultationExportData] = useState(null);
  const [titrePage, setTitrePage] = useState('Aperçu du Jour'); // État pour le titre de la page

  const currentPeriodeRef = useRef(periode);

  // État pour les vraies données de ventes par conseillère
  const [vraiesVentesConseilleres, setVraiesVentesConseilleres] = useState([]);
  const [loadingVentes, setLoadingVentes] = useState(false);

  // État pour les données personnelles de la conseillère connectée
  const [mesVentesData, setMesVentesData] = useState({
    mesFactures: [],
    monTotalTTC: 0,
    monNombreFactures: 0
  });
  const [loadingMesVentes, setLoadingMesVentes] = useState(false);

  // États pour les consultations effectuées
  const [consultationsData, setConsultationsData] = useState({
    totalConsultations: 0,
    consultationsRecentes: [],
    evolution: '+0%'
  });
  const [loadingConsultations, setLoadingConsultations] = useState(false);

  // État pour les consultations par conseillère (pour admin seulement)
  const [consultationsParConseillere, setConsultationsParConseillere] = useState([]);
  const consultationRequestRef = useRef(0);
  const [loadingConsultationsConseilleres, setLoadingConsultationsConseilleres] = useState(false);

  // Répartition des statuts de leads par conseillère et secrétaire (vue responsable uniquement)
  const [statutsLeadsParConseillere, setStatutsLeadsParConseillere] = useState({
    responsables: [],
    statuts: [],
    totalLeadsAssignes: 0
  });
  const [loadingStatutsLeads, setLoadingStatutsLeads] = useState(false);
  const [filtreStatutsLeads, setFiltreStatutsLeads] = useState({ dateDebut: '', dateFin: '' });
  const filtreStatutsLeadsRef = useRef({ periode: 'jour' });
  const requeteStatutsLeadsRef = useRef(0);

  // Vue consolidée : chaque chiffre est calculé côté serveur, dans l'entreprise
  // de la session, pour la période sélectionnée en haut du tableau de bord.
  const [vueGlobale, setVueGlobale] = useState(EMPTY_VUE_GLOBALE);
  const [loadingVueGlobale, setLoadingVueGlobale] = useState(false);

  // États pour les rendez-vous à venir
  const [vraiesRendezVousAVenir, setVraiesRendezVousAVenir] = useState([]);
  const [loadingRendezVous, setLoadingRendezVous] = useState(false);

  // États pour les activités récentes en temps réel
  const [vraiesActivitesRecentes, setVraiesActivitesRecentes] = useState([]);
  const [loadingActivites, setLoadingActivites] = useState(false);
  const [derniereMAJActivites, setDerniereMAJActivites] = useState(null);

  // États pour le modal client/lead
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [modalType, setModalType] = useState('client'); // 'client' ou 'lead'
  const [loadingClient, setLoadingClient] = useState(false);

  // Mettre à jour le titre de la page lorsque la période change
  useEffect(() => {
    let nouveauTitre = 'Aperçu ';
    switch (periode) {
      case 'jour':
        nouveauTitre += 'du Jour';
        break;
      case 'semaine':
        nouveauTitre += 'de la Semaine';
        break;
      case 'mois':
        nouveauTitre += 'du Mois';
        break;
      case 'trimestre':
        nouveauTitre += 'du Trimestre';
        break;
      case 'annee':
        nouveauTitre += "de l'Année"; // Utilisation de guillemets doubles pour éviter les problèmes d'échappement
        break;
      default:
        nouveauTitre += 'du Mois'; // Valeur par défaut
    }
    setTitrePage(nouveauTitre);
  }, [periode]); // Se déclenche à chaque changement de 'periode'
  
  // État pour les statistiques et données du tableau de bord
  const [stats, setStats] = useState({
    // Statistiques générales
    totalLeads: 0,
    leadsActifs: 0,
    tauxConversion: 0,
    totalClients: 0,
    clientsActifs: 0,
    dossiersEnCours: 0,
    dossiersCompletes: 0,
    facturesEnAttente: 0,
    montantFacturesEnAttente: 0,
    montantFacturesPaye: 0,
    rendezVousAVenir: 0,
    chargesAVenir: 0,
    montantChargesAVenir: 0,
    
    // Statistiques spécifiques aux conseillères
    mesClients: 0,
    mesLeads: 0,
    mesDossiers: 0,
    mesFacturesImpayees: 0,
    mesConsultations: 0,
    mesVentes: 0,
    
    // Statistiques spécifiques au comptable
    totalVentes: 0,
    paiementsRecents: 0,
    
    // Statistiques spécifiques à la secrétaire
    leadsATraiter: 0,
    rendezVousJour: 0,
    nouveauxLeads: 0,
    
    // Statistiques par conseillère (pour admin/directeur) - sera chargé depuis l'API
    ventesParConseillere: [],

    // Évolution (sera calculée dynamiquement)
    evolutionClients: 0,
    evolutionLeads: 0
  });
  
  // Données complètes pour les graphiques (toutes périodes)
  const [allGraphData, setAllGraphData] = useState({
    // Données pour les leads par source
    leadsParSource: {
      jour: [],
      semaine: [],
      mois: [],
      trimestre: [],
      annee: []
    },
    
    // Données pour les revenus par mois
    revenusParMois: {
      jour: [],
      semaine: [],
      mois: [],
      trimestre: [],
      annee: []
    },
    
    // Données pour les dossiers par type
    dossiersParType: {
      jour: [],
      semaine: [],
      mois: [],
      trimestre: [],
      annee: []
    },
    
    // Données pour les ventes par conseillère
    ventesParConseillere: {
      jour: [],
      semaine: [],
      mois: [],
      trimestre: [],
      annee: []
    }
  });
  
  // Données filtrées pour les graphiques selon la période sélectionnée
  const [graphData, setGraphData] = useState({
    leadsParSource: [],
    revenusParMois: [],
    dossiersParType: [],
    ventesParConseillere: []
  });
  
  // Activités récentes pour admin/directeur
  const [activitesRecentes, setActivitesRecentes] = useState([]);

  // Mes activités récentes (pour les conseillères)
  const [mesActivitesRecentes, setMesActivitesRecentes] = useState([]);
  
  // Rendez-vous à venir pour admin/directeur
  const [rendezVousAVenir, setRendezVousAVenir] = useState([]);

  // Mes rendez-vous à venir (pour les conseillères)
  const [mesRendezVousAVenir, setMesRendezVousAVenir] = useState([]);

  // Rendez-vous du jour (pour la secrétaire)
  const [rendezVousJour, setRendezVousJour] = useState([]);

  // Nouveaux leads (pour la secrétaire)
  const [nouveauxLeads, setNouveauxLeads] = useState([]);

  // Dernières factures (pour le comptable)
  const [dernieresFactures, setDernieresFactures] = useState([]);

  // Paiements récents (pour le comptable)
  const [paiementsRecents, setPaiementsRecents] = useState([]);

  // Charges (pour le comptable)
  const [charges, setCharges] = useState([
    {
      id: 'C-2025-023',
      description: 'Loyer bureau',
      montant: 3500,
      date: '2025-05-01',
      statut: 'Payée'
    },
    {
      id: 'C-2025-024',
      description: 'Services publics',
      montant: 850,
      date: '2025-05-05',
      statut: 'Payée'
    },
    {
      id: 'C-2025-025',
      description: 'Fournitures de bureau',
      montant: 450,
      date: '2025-05-12',
      statut: 'En attente'
    },
    {
      id: 'C-2025-026',
      description: 'Services informatiques',
      montant: 1200,
      date: '2025-05-15',
      statut: 'En attente'
    }
  ]);
  
  // Tâches en attente (pour les conseillères)
  const [tachesEnAttente, setTachesEnAttente] = useState([]);
  
  // Fonction pour obtenir le nombre de consultations d'une conseillère
  const obtenirNombreConsultations = (nomConseillere) => {
    // Vérifier que consultationsParConseillere est un tableau
    if (!Array.isArray(consultationsParConseillere)) {
      console.log('⚠️ consultationsParConseillere n\'est pas un tableau:', consultationsParConseillere);
      return 0;
    }
    
    console.log('🔍 Recherche consultations pour:', nomConseillere, 'dans:', consultationsParConseillere);
    const consultation = consultationsParConseillere.find(c => c.conseillere === nomConseillere);
    const nombre = consultation ? consultation.nombreConsultations : 0;
    console.log('📊 Nombre de consultations trouvé:', nombre, 'pour:', nomConseillere);
    return nombre;
  };

  // Fonction pour fusionner les ventes, consultations et membres actifs de
  // l'équipe. Une secrétaire doit rester visible au responsable, même si elle
  // n'a ni facture ni consultation dans la période.
  const listeComplete = useMemo(() => {
    console.log('🔧 [listeComplete useMemo] Recalcul - userRole:', userRole);
    console.log('🔧 [listeComplete useMemo] vraiesVentesConseilleres:', vraiesVentesConseilleres);
    console.log('🔧 [listeComplete useMemo] consultationsParConseillere:', consultationsParConseillere);
    
    if (!DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      console.log('🔧 [listeComplete useMemo] Pas admin, retour ventes uniquement');
      return vraiesVentesConseilleres;
    }

    const normaliserNomPersonne = (value) => String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLocaleLowerCase('fr-FR');

    // Créer une Map pour éviter les doublons, y compris lorsque les anciennes
    // données de facture n'emploient pas exactement la même casse du nom.
    const personnesMap = new Map();
    const ajouterPersonne = (personne = {}) => {
      const nom = personne.conseillere || personne.nom;
      const cle = normaliserNomPersonne(nom);
      if (!cle) return;

      const existante = personnesMap.get(cle) || {};
      personnesMap.set(cle, {
        conseillere: existante.conseillere || nom,
        role: personne.role || existante.role || 'conseillere',
        valeur: Number(existante.valeur ?? personne.valeur ?? 0),
        nombreFactures: Number(existante.nombreFactures ?? personne.nombreFactures ?? 0),
        evolution: Number(existante.evolution ?? personne.evolution ?? 0),
        nombreConsultations: Math.max(
          Number(existante.nombreConsultations || 0),
          Number(personne.nombreConsultations || 0)
        ),
        totalLeads: Math.max(
          Number(existante.totalLeads || 0),
          Number(personne.totalLeads || 0)
        )
      });
    };
    
    // Ajouter les personnes avec des ventes
    vraiesVentesConseilleres.forEach(vente => {
      ajouterPersonne({
        ...vente,
        hasVentes: true
      });
    });
    
    console.log('🔧 [listeComplete useMemo] Après ajout ventes, Map size:', personnesMap.size);
    
    // Ajouter les personnes avec des consultations mais sans ventes
    if (Array.isArray(consultationsParConseillere)) {
      consultationsParConseillere.forEach(consult => {
        console.log('🔧 [listeComplete useMemo] Traitement consultation:', consult.conseillere);
        ajouterPersonne(consult);
      });
    }

    // La répartition par responsable est la liste de référence pour les
    // conseillères ET secrétaires actives du bureau connecté.
    (statutsLeadsParConseillere.responsables || []).forEach((responsable) => {
      ajouterPersonne({
        nom: responsable.nom,
        role: responsable.role,
        totalLeads: responsable.totalLeads
      });
    });
    
    console.log('🔧 [listeComplete useMemo] Après ajout consultations, Map size:', personnesMap.size);
    
    // Convertir en tableau et trier (ventes d'abord, puis consultations)
    const result = Array.from(personnesMap.values()).sort((a, b) => {
      // D'abord par nombre de ventes (descendant)
      if (b.valeur !== a.valeur) return b.valeur - a.valeur;
      // Ensuite par nombre de consultations (descendant)
      const consultA = a.nombreConsultations || 0;
      const consultB = b.nombreConsultations || 0;
      return consultB - consultA;
    });
    
    console.log('🔧 [listeComplete useMemo] Résultat final:', result);
    return result;
  }, [userRole, vraiesVentesConseilleres, consultationsParConseillere, statutsLeadsParConseillere.responsables]);
  
  // Fonction pour formater les montants en devise
  const formaterMontant = (montant) => {
    return new Intl.NumberFormat('fr-CA', { style: 'currency', currency: 'CAD' }).format(montant);
  };

  const formaterMontantsParDevise = (montants = []) => {
    if (!Array.isArray(montants) || montants.length === 0) return '—';

    return montants.map(({ monnaie, montant }) => new Intl.NumberFormat('fr-MA', {
      style: 'currency',
      currency: monnaie === 'CAD' ? 'CAD' : 'MAD',
      maximumFractionDigits: 2
    }).format(Number(montant) || 0)).join(' · ');
  };

  const formaterPourcentage = (valeur) => `${Number(valeur || 0).toLocaleString('fr-FR', {
    maximumFractionDigits: 1
  })} %`;

  const exporterTableauDeBord = () => {
    if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole) && !consultationExportData) {
      showWarning('Attendez le chargement des consultations avant l’export.'); return;
    }
    if (loadingStatutsLeads || loadingVueGlobale || loadingVentes || loadingConsultationsConseilleres) {
      showWarning('Patientez pendant le chargement des statistiques avant de les exporter.');
      return;
    }
    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const ajouterLigne = (lignes, valeurs) => lignes.push(valeurs.map(escapeCsv).join(';'));
    const libellePeriode = {
      jour: 'Jour',
      semaine: 'Semaine',
      mois: 'Mois',
      trimestre: 'Trimestre',
      annee: 'Année'
    }[periode] || periode;
    const lignes = [];
    const leadsVueGlobale = vueGlobale.leads || EMPTY_VUE_GLOBALE.leads;
    const dossiersVueGlobale = vueGlobale.dossiers || EMPTY_VUE_GLOBALE.dossiers;
    const rendezVousVueGlobale = vueGlobale.rendezVous || EMPTY_VUE_GLOBALE.rendezVous;
    const facturationVueGlobale = vueGlobale.facturation || EMPTY_VUE_GLOBALE.facturation;
    const facturesParStatut = facturationVueGlobale.statuts || EMPTY_VUE_GLOBALE.facturation.statuts;
    const comparaisonVueGlobale = vueGlobale.comparaison || EMPTY_VUE_GLOBALE.comparaison;
    const relancesVueGlobale = vueGlobale.relances || EMPTY_VUE_GLOBALE.relances;

    ajouterLigne(lignes, ['TABLEAU DE BORD', titrePage]);
    ajouterLigne(lignes, ['Période', libellePeriode]);
    ajouterLigne(lignes, ['Exporté le', new Date().toLocaleString('fr-FR')]);
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['INDICATEURS DE PILOTAGE']);
    ajouterLigne(lignes, ['Indicateur', 'Valeur', 'Détail']);
    ajouterLigne(lignes, ['Leads reçus', leadsVueGlobale.total, `${formaterPourcentage(leadsVueGlobale.tauxConversion)} convertis en clients`]);
    ajouterLigne(lignes, ['Leads contactés', leadsVueGlobale.contactes, '']);
    ajouterLigne(lignes, ['Rendez-vous pris', leadsVueGlobale.rendezVousPris, '']);
    ajouterLigne(lignes, ['Dossiers créés', dossiersVueGlobale.nouveaux, `${dossiersVueGlobale.enCours} en cours`]);
    ajouterLigne(lignes, ['Rendez-vous', rendezVousVueGlobale.total, `${rendezVousVueGlobale.realises} réalisés · ${rendezVousVueGlobale.aVenir} à venir`]);
    ajouterLigne(lignes, ['Montant encaissé', formaterMontantsParDevise(facturationVueGlobale.encaisse), `${facturationVueGlobale.total} facture(s)`]);
    ajouterLigne(lignes, ['Montant à encaisser', formaterMontantsParDevise(facturationVueGlobale.aEncaisser), '']);
    ajouterLigne(lignes, ['Statut de facture', 'Nombre', '']);
    [
      ['brouillon', 'Brouillons'],
      ['payable', 'À payer'],
      ['partiellement_payee', 'Partiellement payées'],
      ['payee', 'Payées'],
      ['en_retard', 'En retard'],
      ['annulee', 'Annulées']
    ].forEach(([statut, libelle]) => {
      ajouterLigne(lignes, [libelle, facturesParStatut[statut]?.nombre || 0, '']);
    });
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['COMPARAISON AVEC LA PÉRIODE PRÉCÉDENTE']);
    ajouterLigne(lignes, ['Indicateur', 'Période actuelle', 'Période précédente', 'Évolution']);
    const formaterEvolutionCsv = (valeur) => valeur === null || valeur === undefined
      ? '—'
      : `${Number(valeur) > 0 ? '+' : ''}${formaterPourcentage(valeur)}`;
    ajouterLigne(lignes, [
      'Leads reçus',
      leadsVueGlobale.total,
      comparaisonVueGlobale.leads?.precedent || 0,
      formaterEvolutionCsv(comparaisonVueGlobale.leads?.evolution)
    ]);
    ajouterLigne(lignes, [
      'Conversions en clients',
      leadsVueGlobale.convertis,
      comparaisonVueGlobale.conversions?.precedent || 0,
      formaterEvolutionCsv(comparaisonVueGlobale.conversions?.evolution)
    ]);
    (comparaisonVueGlobale.revenus || []).forEach((revenu) => {
      ajouterLigne(lignes, [
        `Revenus encaissés (${revenu.monnaie})`,
        revenu.actuel || 0,
        revenu.precedent || 0,
        formaterEvolutionCsv(revenu.evolution)
      ]);
    });
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['RELANCES À TRAITER']);
    ajouterLigne(lignes, ['Indicateur', 'Nombre']);
    ajouterLigne(lignes, ['Sans activité depuis au moins 3 jours', relancesVueGlobale.sansActivite3Jours || 0]);
    ajouterLigne(lignes, ['Sans activité depuis au moins 7 jours', relancesVueGlobale.sansActivite7Jours || 0]);
    ajouterLigne(lignes, ['Leads nouveaux à relancer', relancesVueGlobale.nouveaux || 0]);
    ajouterLigne(lignes, ['À recontacter', relancesVueGlobale.aRecontacter || 0]);
    ajouterLigne(lignes, ['Injoignables à rappeler', relancesVueGlobale.injoignables || 0]);
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['LEADS PAR SOURCE']);
    ajouterLigne(lignes, ['Source', 'Nombre de leads']);
    graphData.leadsParSource.forEach(({ source, valeur }) => ajouterLigne(lignes, [source || 'Non défini', valeur]));
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['REVENUS ENCAISSÉS']);
    ajouterLigne(lignes, ['Période', 'Montant']);
    graphData.revenusParMois.forEach(({ mois, valeur }) => ajouterLigne(lignes, [mois || 'Non défini', valeur]));
    ajouterLigne(lignes, []);

    ajouterLigne(lignes, ['DOSSIERS PAR TYPE']);
    ajouterLigne(lignes, ['Type', 'Nombre de dossiers']);
    graphData.dossiersParType.forEach(({ type, valeur }) => ajouterLigne(lignes, [type || 'Non défini', valeur]));

    if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      ajouterLigne(lignes, []);
      ajouterLigne(lignes, ['RÉPARTITION DES LEADS PAR RESPONSABLE (STATUT ACTUEL, DATE DE CRÉATION DU LEAD)']);
      ajouterLigne(lignes, ['Filtre', filtreStatutsLeadsRef.current.dateDebut || '', filtreStatutsLeadsRef.current.dateFin || '', filtreStatutsLeadsRef.current.periode || 'Personnalisé']);
      ajouterLigne(lignes, ['Responsable', 'Rôle', 'Statut', 'Nombre', 'Pourcentage']);
      statutsLeadsParConseillere.responsables.forEach((responsable) => {
        if (responsable.statuts?.length) {
          responsable.statuts.forEach((statut) => {
            ajouterLigne(lignes, [
              responsable.nom,
              responsable.role === 'secretaire' ? 'Secrétaire' : 'Conseillère',
              statut.statut,
              statut.nombre,
              formaterPourcentage(statut.pourcentage)
            ]);
          });
        } else {
          ajouterLigne(lignes, [
            responsable.nom,
            responsable.role === 'secretaire' ? 'Secrétaire' : 'Conseillère',
            'Aucun lead attribué',
            0,
            '0 %'
          ]);
        }
      });
    }

    if (consultationExportData) consultationExportRows(consultationExportData).forEach(row => ajouterLigne(lignes, row));
    const fichier = new Blob([`\uFEFF${lignes.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(fichier);
    const lien = document.createElement('a');
    lien.href = url;
    lien.download = `tableau-de-bord-${periode}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(lien);
    lien.click();
    lien.remove();
    URL.revokeObjectURL(url);
    showSuccess('Export du tableau de bord téléchargé.');
  };
  
  // Fonction pour obtenir l'icône selon le type d'activité
  const getIconeActivite = (type) => {
    switch(type) {
      case 'lead': return '👤';
      case 'client': return '👥';
      case 'dossier': return '📁';
      case 'facture': return '💰';
      case 'rendez-vous': return '📅';
      default: return '📝';
    }
  };
  
  // Fonction pour obtenir la classe de priorité
  const getClassePriorite = (priorite) => {
    switch(priorite) {
      case 'haute': return 'priorite-haute';
      case 'moyenne': return 'priorite-moyenne';
      case 'basse': return 'priorite-basse';
      default: return '';
    }
  };
  
  // Fonction pour calculer dynamiquement les statistiques selon la période
  const calculerStatistiques = (nouvellePeriode) => {
    // Obtenir la date actuelle
    const maintenant = new Date();
    
    // Calculer les dates de début et de fin selon la période
    let dateDebut, dateFin;
    dateFin = new Date(maintenant);
    
    switch(nouvellePeriode) {
      case 'jour':
        dateDebut = new Date(maintenant);
        dateDebut.setHours(0, 0, 0, 0);
        break;
      case 'semaine':
        dateDebut = new Date(maintenant);
        dateDebut.setDate(dateDebut.getDate() - 7);
        break;
      case 'mois':
        dateDebut = new Date(maintenant);
        dateDebut.setMonth(dateDebut.getMonth() - 1);
        break;
      case 'trimestre':
        dateDebut = new Date(maintenant);
        dateDebut.setMonth(dateDebut.getMonth() - 3);
        break;
      case 'annee':
        dateDebut = new Date(maintenant);
        dateDebut.setFullYear(dateDebut.getFullYear() - 1);
        break;
      default:
        dateDebut = new Date(maintenant);
        dateDebut.setMonth(dateDebut.getMonth() - 1); // Par défaut: mois
    }
    
    // Dans une application réelle, ces données seraient récupérées via une API
    // en utilisant les dates calculées comme paramètres de filtrage
    
    // Pour cette démonstration, nous utilisons les données pré-calculées
    // stockées dans allGraphData selon la période sélectionnée
    return {
      graphData: {
        leadsParSource: allGraphData.leadsParSource[nouvellePeriode] || [],
        revenusParMois: allGraphData.revenusParMois[nouvellePeriode] || [],
        dossiersParType: allGraphData.dossiersParType[nouvellePeriode] || [],
        ventesParConseillere: allGraphData.ventesParConseillere[nouvellePeriode] || []
      },
      stats: getStatsByPeriode(nouvellePeriode)
    };
  };
  
  // Fonction pour obtenir les statistiques selon la période
  const getStatsByPeriode = (periode) => {
    // Les données sont maintenant chargées dynamiquement via l'API
    // Cette fonction retourne des valeurs vides en attendant les vraies données
    
    return {
      totalLeads: 0,
      leadsActifs: 0,
      tauxConversion: 0,
      totalClients: 0,
      clientsActifs: 0,
      dossiersEnCours: 0,
      dossiersCompletes: 0,
      facturesEnAttente: 0,
      montantFacturesEnAttente: 0,
      montantFacturesPaye: 0,
      rendezVousAVenir: 0,
      chargesAVenir: 0,
      montantChargesAVenir: 0,
      // Statistiques spécifiques aux conseillères
      mesClients: 0,
      mesLeads: 0,
      mesDossiers: 0,
      mesFacturesImpayees: 0,
      mesConsultations: 0,
      mesVentes: 0,
      // Statistiques spécifiques au comptable
      totalVentes: 0,
      paiementsRecents: 0,
      // Statistiques spécifiques à la secrétaire
      leadsATraiter: 0,
      rendezVousJour: 0,
      nouveauxLeads: 0,
      // Statistiques par conseillère
      ventesParConseillere: []
    };
  };
  
  // Fonction pour charger les vraies données de ventes par conseillère (pour admin/directeur)
  const chargerVraiesVentes = async (periodeChoisie = periode) => {
    try {
      setLoadingVentes(true);
      console.log(`🔄 [TableauBord] Chargement ventes conseillères pour période: ${periodeChoisie}, userRole: ${userRole}`);
      
      const result = await getVentesParConseillere(periodeChoisie);
      console.log(`📊 [TableauBord] Résultat API ventes:`, result);
      
      if (result.success) {
        console.log(`✅ [TableauBord] ${result.data.length} ventes chargées:`, result.data);
        setVraiesVentesConseilleres(result.data);
        
        // Mettre à jour les stats avec les vraies données
        setStats(prev => ({
          ...prev,
          ventesParConseillere: result.data,
          totalVentes: result.summary?.totalVentesTTC || prev.totalVentes
        }));
        
        // Mettre à jour aussi les données des graphiques
        setGraphData(prev => ({
          ...prev,
          ventesParConseillere: result.data
        }));
      } else {
        console.error('❌ [TableauBord] Erreur chargement ventes:', result.error);
        // En cas d'erreur, garder les données vides
        setVraiesVentesConseilleres([]);
      }
    } catch (error) {
      console.error('❌ [TableauBord] Erreur chargement ventes conseillères:', error);
      setVraiesVentesConseilleres([]);
    } finally {
      setLoadingVentes(false);
    }
  };

  // Fonction pour charger MES données de ventes (pour conseillères)
  const chargerMesVentes = async (periodeChoisie = periode) => {
    try {
      setLoadingMesVentes(true);
      
      // 🔴 AMÉLIORATION : Logique robuste pour déterminer le nom d'utilisateur
      let nomUtilisateur = userName;
      
      // Essayer différentes sources pour le nom d'utilisateur
      if (!nomUtilisateur || nomUtilisateur === 'Utilisateur Test') {
        const storedUserName = localStorage.getItem('userName');
        const storedUser = localStorage.getItem('user');
        const storedFullName = localStorage.getItem('fullName');
        
        if (storedUserName) {
          nomUtilisateur = storedUserName;
        } else if (storedUser) {
          try {
            const userData = JSON.parse(storedUser);
            nomUtilisateur = userData.nom || userData.prenom || userData.username || nomUtilisateur;
          } catch (e) {
            console.warn('⚠️ Erreur parsing user data du localStorage');
          }
        } else if (storedFullName) {
          nomUtilisateur = storedFullName;
        }
      }
      
      console.log(`🔄 Chargement MES ventes pour: "${nomUtilisateur}" (original: "${userName}"), période: ${periodeChoisie}`);
      console.log(`🔍 Sources testées: localStorage.userName="${localStorage.getItem('userName')}", localStorage.user="${localStorage.getItem('user')?.substring(0, 50)}..."`);
      
      const result = await getMesVentes(nomUtilisateur, periodeChoisie);
      
      if (result.success) {
        console.log(`✅ Mes ventes chargées:`, result.data);
        setMesVentesData(result.data);
        
        // Mettre à jour les stats avec mes données
        setStats(prev => ({
          ...prev,
          mesClients: result.data.monNombreFactures || 0, // Approximation
          mesFacturesImpayees: result.data.facturesEnAttente || 0,
          mesVentes: result.data.facturesPayees || 0,
          totalMesVentes: result.data.monTotalTTC || 0
        }));
      } else {
        console.error('❌ Erreur chargement mes ventes:', result.error);
      }
    } catch (error) {
      console.error('❌ Erreur chargement mes ventes:', error);
    } finally {
      setLoadingMesVentes(false);
    }
  };

  // Fonction pour charger les vrais rendez-vous à venir
  const chargerRendezVousAVenir = async () => {
    try {
      setLoadingRendezVous(true);
      console.log(`🔄 [TableauBord] Chargement rendez-vous à venir pour userRole: ${userRole}, userName: ${userName}`);
      
      const result = await getRendezVousAVenir(userRole, userName, 10);
      console.log(`📅 [TableauBord] Résultat API rendez-vous:`, result);
      
      if (result.success) {
        console.log(`✅ [TableauBord] ${result.data.length} rendez-vous chargés:`, result.data);
        setVraiesRendezVousAVenir(result.data);
      } else {
        console.error('❌ [TableauBord] Erreur chargement rendez-vous:', result.error);
        // En cas d'erreur, garder les données vides pour utiliser les données statiques
        setVraiesRendezVousAVenir([]);
      }
    } catch (error) {
      console.error('❌ [TableauBord] Erreur chargement rendez-vous à venir:', error);
      setVraiesRendezVousAVenir([]);
    } finally {
      setLoadingRendezVous(false);
    }
  };

  // Fonction pour charger les rendez-vous d'aujourd'hui
  const chargerRendezVousAujourdhui = async () => {
    try {
      console.log(`🔄 [TableauBord] Chargement rendez-vous d'aujourd'hui`);
      
      const result = await getRendezVousAujourdhui();
      console.log(`📅 [TableauBord] Résultat API rendez-vous aujourd'hui:`, result);
      
      if (result.success) {
        console.log(`✅ [TableauBord] ${result.count} rendez-vous aujourd'hui:`, result.data);
        // 🔍 DEBUG: Afficher chaque rendez-vous avec son lead_id
        result.data.forEach((rdv, index) => {
          console.log(`📋 [RDV ${index + 1}] Client: "${rdv.client}", lead_id: ${rdv.lead_id}, Toutes les données:`, rdv);
        });
        setRendezVousJour(result.data);
        // Mettre à jour aussi les stats
        setStats(prev => ({ ...prev, rendezVousJour: result.count }));
      } else {
        console.error('❌ [TableauBord] Erreur chargement rendez-vous aujourd\'hui:', result.error);
        setRendezVousJour([]);
      }
    } catch (error) {
      console.error('❌ [TableauBord] Erreur chargement rendez-vous aujourd\'hui:', error);
      setRendezVousJour([]);
    }
  };

  // Fonction pour charger les vraies activités récentes en temps réel
  const chargerActivitesRecentes = async () => {
    try {
      setLoadingActivites(true);
      console.log(`🔄 [TableauBord] Chargement activités récentes pour userRole: ${userRole}, userName: ${userName}`);
      console.log(`🔑 [TableauBord] Token présent: ${!!localStorage.getItem('token')}`);
      
      const result = await getActivitesRecentes(10);
      console.log(`📝 [TableauBord] Résultat API activités:`, result);
      
      if (result.success && result.data && result.data.length > 0) {
        console.log(`✅ [TableauBord] ${result.data.length} activités chargées:`, result.data);
        setVraiesActivitesRecentes(result.data);
        setDerniereMAJActivites(result.lastUpdate || new Date().toISOString());
        
        // Mettre à jour les activités statiques avec les vraies données
        if (userRole === 'directeur' || userRole === 'administrateur' || userRole === 'manager') {
          setActivitesRecentes(result.data);
        } else if (userRole === 'conseillere') {
          setMesActivitesRecentes(result.data);
        }
      } else {
        console.warn('⚠️ [TableauBord] Aucune activité récente ou erreur API:', result.message || 'Aucune donnée');
        console.log('📊 [TableauBord] Conservation des activités statiques pour l\'affichage');
        // En cas d'erreur, garder les données statiques - NE PAS les vider
        setVraiesActivitesRecentes([]);
        // Les activités statiques restent intactes pour l'affichage
      }
    } catch (error) {
      console.error('❌ [TableauBord] Erreur chargement activités récentes:', error);
      console.error('🔍 [TableauBord] Détails erreur:', error.message, error.stack);
      console.warn('⚠️ [TableauBord] Fallback: conservation des activités statiques');
      setVraiesActivitesRecentes([]);
      // Les activités statiques restent pour l'affichage
    } finally {
      setLoadingActivites(false);
    }
  };

  // Fonction pour charger les consultations effectuées
  const chargerConsultations = async (periodeChoisie = periode) => {
    try {
      setLoadingConsultations(true);
      console.log(`🔄 Chargement consultations pour: ${userName}, période: ${periodeChoisie}`);
      
      // 🔧 CORRECTION: Utiliser d'abord l'ancien système fiable
      const userId = localStorage.getItem('userId');
      const result = await getConsultations(userRole, userId, periodeChoisie, userName);
      
      console.log('📊 [TableauBord] Résultat consultations:', result);
      
      if (result.success) {
        console.log(`✅ Consultations chargées:`, result.data);
        setConsultationsData(result.data);
        
        // Mettre à jour les stats avec les données de consultations
        setStats(prev => ({
          ...prev,
          mesConsultations: result.data.totalConsultations || 0
        }));
      } else {
        console.error('❌ Erreur chargement consultations:', result.error);
        // Fallback avec valeurs par défaut
        setConsultationsData({
          totalConsultations: 0,
          consultationsRecentes: [],
          evolution: '+0%'
        });
        setStats(prev => ({
          ...prev,
          mesConsultations: 0
        }));
      }
    } catch (error) {
      console.error('❌ Erreur chargement consultations:', error);
      // Fallback avec valeurs par défaut
      setConsultationsData({
        totalConsultations: 0,
        consultationsRecentes: [],
        evolution: '+0%'
      });
      setStats(prev => ({
        ...prev,
        mesConsultations: 0
      }));
    } finally {
      setLoadingConsultations(false);
    }
  };

  // Fonction pour charger les consultations par conseillère (pour admin seulement)
  const chargerConsultationsConseilleres = async (periodeChoisie = periode) => {
    if (!DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      console.log('🚫 Utilisateur non autorisé pour les consultations:', userRole);
      return;
    }

    const request = ++consultationRequestRef.current;
    try {
      setLoadingConsultationsConseilleres(true);
      console.log('🔄 Chargement consultations par conseillère pour période:', periodeChoisie);
      const result = await getConsultationsParConseillere(periodeChoisie);
      if (request !== consultationRequestRef.current) return;
      console.log('📊 Résultat API consultations:', result);
      
      if (result.success && result.data) {
        console.log('✅ Consultations chargées:', result.data);
        setConsultationsParConseillere(result.data);
      } else {
        console.log('⚠️ Pas de données de consultations');
        setConsultationsParConseillere([]);
      }
    } catch (error) {
      if (request !== consultationRequestRef.current) return;
      console.error('❌ Erreur chargement consultations par conseillère:', error);
      setConsultationsParConseillere([]);
    } finally {
      if (request === consultationRequestRef.current) setLoadingConsultationsConseilleres(false);
    }
  };

  const chargerStatutsLeadsParConseillere = async (filters = filtreStatutsLeadsRef.current) => {
    if (!DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      return;
    }

    const requete = ++requeteStatutsLeadsRef.current;
    try {
      setLoadingStatutsLeads(true);
      const result = await getStatutsLeadsParConseillere(filters);
      if (requete !== requeteStatutsLeadsRef.current) return;

      if (result.success) {
        setStatutsLeadsParConseillere(result.data);
      } else {
        setStatutsLeadsParConseillere({
          responsables: [],
          statuts: [],
          totalLeadsAssignes: 0
        });
      }
    } catch (error) {
      if (requete !== requeteStatutsLeadsRef.current) return;
      console.error('❌ Erreur chargement des statuts de leads par conseillère:', error);
      setStatutsLeadsParConseillere({
        responsables: [],
        statuts: [],
        totalLeadsAssignes: 0
      });
    } finally {
      if (requete === requeteStatutsLeadsRef.current) setLoadingStatutsLeads(false);
    }
  };

  const appliquerFiltreStatutsLeads = () => {
    const prochainFiltre = filtreStatutsLeads.dateDebut || filtreStatutsLeads.dateFin
      ? { ...filtreStatutsLeads }
      : { periode };
    if (prochainFiltre.dateDebut && prochainFiltre.dateFin && prochainFiltre.dateDebut > prochainFiltre.dateFin) {
      showWarning('La date de début doit être antérieure ou égale à la date de fin.');
      return;
    }

    filtreStatutsLeadsRef.current = prochainFiltre;
    chargerStatutsLeadsParConseillere(prochainFiltre);
  };

  const reinitialiserFiltreStatutsLeads = () => {
    const prochainFiltre = { periode };
    setFiltreStatutsLeads({ dateDebut: '', dateFin: '' });
    filtreStatutsLeadsRef.current = prochainFiltre;
    chargerStatutsLeadsParConseillere(prochainFiltre);
  };

  const chargerVueGlobale = async (periodeChoisie = periode) => {
    if (!DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) return;

    try {
      setLoadingVueGlobale(true);
      const result = await getVueGlobaleDashboard(periodeChoisie);
      if (result.success && result.data) {
        setVueGlobale(result.data);
      } else {
        setVueGlobale(EMPTY_VUE_GLOBALE);
      }
    } catch (error) {
      console.error('❌ Erreur chargement de la vue globale du tableau de bord:', error);
      setVueGlobale(EMPTY_VUE_GLOBALE);
    } finally {
      setLoadingVueGlobale(false);
    }
  };

  // Fonction pour charger les statistiques dynamiques depuis l'API
  const chargerStatistiquesDynamiques = async (nouvellePeriode) => {
    try {
      console.log(`🔄 [TableauBord] Chargement des statistiques GLOBALES (tous utilisateurs) pour la période: ${nouvellePeriode}`);
      
      const result = await statisticsService.getGlobalStatisticsWithFallback(nouvellePeriode);
      
      console.log(`📊 [TableauBord] Statistiques GLOBALES reçues:`, result);
      
      // Calculer les totaux pour affichage dans la console
      const totalLeads = result.leadsParSource ? result.leadsParSource.reduce((sum, item) => sum + item.valeur, 0) : 0;
      const totalRevenus = result.revenusParMois ? result.revenusParMois.reduce((sum, item) => sum + item.valeur, 0) : 0;
      const totalDossiers = result.dossiersParType ? result.dossiersParType.reduce((sum, item) => sum + item.valeur, 0) : 0;
      
      console.log(`🎯 [TableauBord] Résumé statistiques GLOBALES ${nouvellePeriode}:`);
      console.log(`   👥 Total leads (tous utilisateurs): ${totalLeads}`);
      console.log(`   💰 Total revenus (tous utilisateurs): ${totalRevenus.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD' })}`);
      console.log(`   📁 Total dossiers (tous utilisateurs): ${totalDossiers}`);
      
      // Mettre à jour les données des graphiques avec les vraies données globales
      setGraphData({
        leadsParSource: result.leadsParSource || [],
        revenusParMois: result.revenusParMois || [],
        dossiersParType: result.dossiersParType || [],
        ventesParConseillere: result.ventesParConseillere || []
      });
      
      console.log(`✅ [TableauBord] Graphiques mis à jour avec les données GLOBALES dynamiques`);
    } catch (error) {
      console.error('❌ [TableauBord] Erreur lors du chargement des statistiques globales:', error);
      
      // En cas d'erreur, utiliser les données statiques comme fallback
      const fallbackData = statisticsService.getFallbackData(nouvellePeriode);
      setGraphData({
        leadsParSource: fallbackData.leadsParSource || [],
        revenusParMois: fallbackData.revenusParMois || [],
        dossiersParType: fallbackData.dossiersParType || [],
        ventesParConseillere: fallbackData.ventesParConseillere || []
      });
      
      console.log(`⚡ [TableauBord] Utilisation des données de fallback`);
    }
  };

  // Fonction pour changer la période des statistiques
  const changerPeriode = async (nouvellePeriode) => {
    setPeriode(nouvellePeriode);
    setFiltreStatutsLeads({ dateDebut: '', dateFin: '' });
    filtreStatutsLeadsRef.current = { periode: nouvellePeriode };
    if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      chargerStatutsLeadsParConseillere(filtreStatutsLeadsRef.current);
    }
    
    // Charger les statistiques dynamiques pour les graphiques
    await chargerStatistiquesDynamiques(nouvellePeriode);
    
    // Charger les données appropriées selon le rôle
    if (userRole === 'conseillere') {
      chargerMesVentes(nouvellePeriode);
      chargerConsultations(nouvellePeriode);
    } else if (userRole === 'secretaire') {
      chargerConsultations(nouvellePeriode);
    } else {
      chargerVraiesVentes(nouvellePeriode);
      // Charger les consultations par responsable pour tous les rôles de gestion.
      if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
        chargerConsultationsConseilleres(nouvellePeriode);
      }
    }
    
    // Charger aussi les rendez-vous à venir et les activités récentes
    chargerRendezVousAVenir();
    chargerActivitesRecentes();
  };
  
  // Initialiser les données des graphiques au chargement du composant
  useEffect(() => {
    // Charger les statistiques dynamiques pour la période initiale
    chargerStatistiquesDynamiques(periode);
    
    console.log(`🎯 [TableauBord] userRole au démarrage: "${userRole}"`);
    
    // Charger les bonnes données selon le rôle au démarrage
    if (userRole === 'conseillere') {
      chargerMesVentes(periode);
      chargerConsultations(periode);
    } else if (userRole === 'secretaire') {
      chargerConsultations(periode);
    } else {
      chargerVraiesVentes(periode);
      // Charger les consultations par conseillère pour les administrateurs et managers
      console.log(`🔍 [TableauBord] Vérification rôle responsable: userRole="${userRole}", autorisé?`, DASHBOARD_RESPONSIBLE_ROLES.includes(userRole));
      if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
        console.log(`✅ [TableauBord] Appel chargerConsultationsConseilleres`);
        chargerConsultationsConseilleres(periode);
      } else {
        console.log(`❌ [TableauBord] PAS d'appel chargerConsultationsConseilleres - rôle: "${userRole}"`);
      }

      if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
        chargerStatutsLeadsParConseillere();
      }
    }
    
    // Charger les rendez-vous à venir et les activités récentes
    chargerRendezVousAVenir();
    chargerRendezVousAujourdhui(); // Charger les rendez-vous d'aujourd'hui
    chargerActivitesRecentes();
  }, [userRole, userName]); // Dépendances pour recharger si le rôle/nom change
  
  // Récupération des statistiques depuis l'API à chaque changement de période
  useEffect(() => {
    currentPeriodeRef.current = periode;
    let isMounted = true;

    const fetchStats = async () => {
      try {
        console.log('🔍 [TableauBord] Récupération stats pour userRole:', userRole, 'période:', periode);
        const res = await getDashboardStats(periode);
        if (!isMounted) return;

        const data = res?.data ?? {};
        console.log('📊 [TableauBord] Données reçues de l\'API:', data);

        // Si la période a changé pendant la requête, on ignore le résultat
        if (currentPeriodeRef.current !== periode) return;

        console.log('✏️ [TableauBord] Mise à jour stats pour userRole:', userRole);
        setStats(prev => ({
          ...prev,
          totalLeads: data.totalLeads ?? 0,
          totalClients: data.totalClients ?? 0,
          nouveauxLeads: data.nouveauxLeads ?? 0, // ✅ AJOUT: Nouveaux leads
          leadsATraiter: data.leadsATraiter ?? 0, // ✅ AJOUT: Leads à traiter
          // Pour les conseillères, mettre à jour mesLeads et mesClients
          mesLeads: userRole === 'conseillere' ? (data.totalLeads ?? 0) : prev.mesLeads,
          mesClients: userRole === 'conseillere' ? (data.totalClients ?? 0) : prev.mesClients,
          mesDossiers: userRole === 'conseillere' ? (data.dossiersActifs ?? 0) : prev.mesDossiers,
          mesFacturesImpayees: userRole === 'conseillere' ? (data.facturesEnAttente ?? 0) : prev.mesFacturesImpayees,
          dossiersEnCours: data.dossiersActifs ?? 0,
          facturesEnAttente: data.facturesEnAttente ?? 0,
          montantFacturesEnAttente: data.montantFacturesEnAttente ?? 0,
          evolutionLeads: prev.totalLeads ? Math.round(((data.totalLeads ?? 0 - prev.totalLeads) / prev.totalLeads) * 100) : 0,
          evolutionClients: prev.totalClients ? Math.round(((data.totalClients ?? 0 - prev.totalClients) / prev.totalClients) * 100) : 0
        }));
      } catch (err) {
        console.error('Erreur récupération stats:', err);
      }
    };

    fetchStats();

    return () => {
      isMounted = false;
    };
  }, [periode, userRole]); // Ajouter userRole aux dépendances

  // Les blocs consolidés suivent le même filtre de période que les boutons
  // Jour / Semaine / Mois / Trimestre / Année du tableau de bord.
  useEffect(() => {
    if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) {
      chargerVueGlobale(periode);
    }
  }, [periode, userRole]);

  // Fonction pour recharger les statistiques depuis l'API
  const rechargerStatistiques = async () => {
    try {
      console.log('🔄 [TableauBord] Rechargement stats pour userRole:', userRole, 'période:', periode);
      const res = await getDashboardStats(periode);
      const data = res?.data ?? {};
      console.log('📊 [TableauBord] Données rechargées de l\'API:', data);

      setStats(prev => ({
        ...prev,
        totalLeads: data.totalLeads ?? 0,
        totalClients: data.totalClients ?? 0,
        nouveauxLeads: data.nouveauxLeads ?? 0, // ✅ AJOUT: Nouveaux leads
        leadsATraiter: data.leadsATraiter ?? 0, // ✅ AJOUT: Leads à traiter
        // Pour les conseillères, mettre à jour mesLeads et mesClients
        mesLeads: userRole === 'conseillere' ? (data.totalLeads ?? 0) : prev.mesLeads,
        mesClients: userRole === 'conseillere' ? (data.totalClients ?? 0) : prev.mesClients,
        mesDossiers: userRole === 'conseillere' ? (data.dossiersActifs ?? 0) : prev.mesDossiers,
        mesFacturesImpayees: userRole === 'conseillere' ? (data.facturesEnAttente ?? 0) : prev.mesFacturesImpayees,
        dossiersEnCours: data.dossiersActifs ?? 0,
        facturesEnAttente: data.facturesEnAttente ?? 0,
        montantFacturesEnAttente: data.montantFacturesEnAttente ?? 0,
        evolutionLeads: prev.totalLeads ? Math.round(((data.totalLeads ?? 0 - prev.totalLeads) / prev.totalLeads) * 100) : 0,
        evolutionClients: prev.totalClients ? Math.round(((data.totalClients ?? 0 - prev.totalClients) / prev.totalClients) * 100) : 0
      }));

      chargerVueGlobale(periode);
      
      console.log('[Dashboard] Statistiques rechargées:', data);
    } catch (err) {
      console.error('[Dashboard] Erreur rechargement stats:', err);
    }
  };

  // Écoute des évènements temps réel
  useEffect(() => {
    const socket = connectSocket();

    socket.on('leadCreated', () => {
      console.log('[Dashboard] Lead créé - rechargement des stats et activités');
      rechargerStatistiques();
      chargerStatutsLeadsParConseillere();
      chargerActivitesRecentes();
    });

    socket.on('leadUpdated', () => {
      console.log('[Dashboard] Lead mis à jour - rechargement des stats et activités');
      rechargerStatistiques();
      chargerStatutsLeadsParConseillere();
      chargerActivitesRecentes();
    });

    socket.on('clientCreated', () => {
      console.log('[Dashboard] Client créé - rechargement des stats et activités');
      rechargerStatistiques();
      chargerActivitesRecentes();
    });

    socket.on('clientUpdated', () => {
      console.log('[Dashboard] Client mis à jour - rechargement des stats et activités');
      rechargerStatistiques();
      chargerActivitesRecentes();
    });

    socket.on('dossierCreated', () => {
      console.log('[Dashboard] Dossier créé - rechargement des stats et activités');
      rechargerStatistiques();
      chargerActivitesRecentes();
    });

    socket.on('dossierUpdated', () => {
      console.log('[Dashboard] Dossier mis à jour - rechargement des stats et activités');
      rechargerStatistiques();
      chargerActivitesRecentes();
    });

    socket.on('factureCreated', () => {
      console.log('[Dashboard] Facture créée - rechargement des activités et ventes');
      chargerActivitesRecentes();
      // 🔴 NOUVEAU : Recharger aussi les ventes pour le dashboard
      if (userRole === 'conseillere') {
        chargerMesVentes(periode);
      } else {
        chargerVraiesVentes(periode);
      }
      rechargerStatistiques();
    });

    socket.on('factureUpdated', () => {
      console.log('[Dashboard] Facture mise à jour - rechargement des activités et ventes');
      chargerActivitesRecentes();
      // 🔴 NOUVEAU : Recharger aussi les ventes pour le dashboard
      if (userRole === 'conseillere') {
        chargerMesVentes(periode);
      } else {
        chargerVraiesVentes(periode);
      }
      rechargerStatistiques();
    });

    socket.on('rendezVousCreated', () => {
      console.log('[Dashboard] Rendez-vous créé - rechargement des activités');
      chargerActivitesRecentes();
      chargerRendezVousAVenir();
    });

    socket.on('leadDeleted', () => {
      console.log('[Dashboard] Lead supprimé - rechargement des stats et activités');
      rechargerStatistiques();
      chargerStatutsLeadsParConseillere();
      chargerActivitesRecentes();
    });

    socket.on('clientDeleted', () => {
      console.log('[Dashboard] Client supprimé - rechargement des stats et activités');
      rechargerStatistiques();
      chargerActivitesRecentes();
    });

    // 🔴 NOUVEAU: Écouter la synchronisation Google Sheets
    socket.on('googleSheetsSync', (data) => {
      console.log('[Dashboard] Synchronisation Google Sheets détectée:', data);
      if (data.created > 0 || data.updated > 0) {
        console.log(`[Dashboard] ${data.created} leads créés, ${data.updated} leads mis à jour via Google Sheets - rechargement stats`);
        rechargerStatistiques();
        chargerStatutsLeadsParConseillere();
        chargerActivitesRecentes();
      }
    });

    return () => {
      socket.off('leadCreated');
      socket.off('leadUpdated');
      socket.off('clientCreated');
      socket.off('clientUpdated');
      socket.off('dossierCreated');
      socket.off('dossierUpdated');
      socket.off('factureCreated');
      socket.off('factureUpdated');
      socket.off('rendezVousCreated');
      socket.off('leadDeleted');
      socket.off('clientDeleted');
      socket.off('googleSheetsSync');
    };
  }, [periode]);

  // Rafraîchissement automatique des activités récentes toutes les 2 minutes
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log('🔄 [Dashboard] Rafraîchissement automatique des activités récentes');
      chargerActivitesRecentes();
    }, 2 * 60 * 1000); // 2 minutes

    return () => {
      clearInterval(intervalId);
    };
  }, [userRole, userName]);

  // 🔴 NOUVEAU: Rafraîchissement automatique des statistiques toutes les 60 secondes
  // pour capturer les nouveaux leads Google Sheets
  useEffect(() => {
    const intervalId = setInterval(() => {
      console.log('🔄 [Dashboard] Actualisation automatique des statistiques (leads Google Sheets)');
      rechargerStatistiques();
    }, 60 * 1000); // 1 minute

    return () => {
      clearInterval(intervalId);
    };
  }, [userRole, periode]);
  
  // Graphique à barres simple avec CSS.
  // Une période sans données doit rester explicite : auparavant le tableau était vide,
  // ce qui donnait l'impression que le graphique des revenus ne fonctionnait pas.
  const renderBarChart = (data, maxHeight = 150) => {
    const safeData = Array.isArray(data) ? data : [];

    if (safeData.length === 0) {
      return (
        <div className="bar-chart bar-chart-empty" role="status">
          <span>Aucune donnée pour cette période</span>
        </div>
      );
    }

    const maxValue = Math.max(0, ...safeData.map(item => Number(item.valeur) || 0));
    const scale = maxValue || 1;

    return (
      <div className="bar-chart">
        {safeData.map((item, index) => {
          const value = Number(item.valeur) || 0;
          const label = item.source || item.mois || item.type || item.conseillere || 'Non défini';

          return (
            <div key={`${label}-${index}`} className="bar-container" title={`${label} : ${value}`}>
              <div 
                className="bar" 
                style={{ 
                  height: `${value > 0 ? Math.max(3, (value / scale) * maxHeight) : 0}px` 
                }}
              >
                <span className="bar-value">{value}</span>
              </div>
              <div className="bar-label">{label}</div>
            </div>
          );
        })}
      </div>
    );
  };

  // Fonction pour rediriger vers la page correspondante lors du clic sur un compteur
  const naviguerVers = (destination, params = {}) => {
    console.log(`Navigation vers: ${destination}`, params);
    // Cette fonction sera connectée au système de navigation du Dashboard
    if (typeof onNavigate === 'function') {
      onNavigate(destination, params);
    }
  };

  // Fonction pour récupérer et afficher les informations d'un client ou lead
  const afficherInfosContact = async (contactId, contactNom, contactType = 'client') => {
    try {
      setLoadingClient(true);
      console.log(`🔄 [TableauBord] Récupération des informations du ${contactType} ${contactId} - ${contactNom}`);
      
      // Réinitialiser les états
      setSelectedClient(null);
      setSelectedLead(null);
      setModalType(contactType);
      
      if (contactType === 'lead' || (!contactId && contactType !== 'client')) {
        // C'est un lead ou pas d'ID (donc probablement un lead)
        console.log('🎯 [TableauBord] Recherche dans les leads...');
        
        const allLeadsResponse = await getAllLeads();
        if (allLeadsResponse.success && allLeadsResponse.data && allLeadsResponse.data.leads) {
          const lead = allLeadsResponse.data.leads.find(l => 
            `${l.prenom} ${l.nom}` === contactNom || 
            `${l.nom} ${l.prenom}` === contactNom ||
            (contactId && l.id === parseInt(contactId))
          );
          
          if (lead) {
            setSelectedLead(lead);
            setModalType('lead');
            setShowClientModal(true);
            console.log(`✅ [TableauBord] Lead trouvé:`, lead);
            return;
          }
        }
        
        // Si pas trouvé dans les leads et pas d'ID, chercher dans les clients
        if (!contactId) {
          console.log('🔄 [TableauBord] Lead non trouvé, recherche dans les clients...');
          contactType = 'client';
        }
      }
      
      if (contactType === 'client') {
        if (!contactId) {
          console.warn('⚠️ [TableauBord] Aucun ID fourni, tentative de recherche par nom dans les clients');
          // Si pas d'ID, essayer de trouver le client par nom
          const allClientsResponse = await clientsAPI.getAllClients();
          if (allClientsResponse.success && allClientsResponse.data && allClientsResponse.data.clients) {
            const client = allClientsResponse.data.clients.find(c => 
              `${c.prenom} ${c.nom}` === contactNom || 
              `${c.nom} ${c.prenom}` === contactNom
            );
            
            if (client) {
              setSelectedClient(client);
              setModalType('client');
              setShowClientModal(true);
              console.log(`✅ [TableauBord] Client trouvé par nom:`, client);
              return;
            }
          }
        } else {
          // Récupérer le client par ID
          const response = await clientsAPI.getClientById(contactId);
          
          if (response.success && response.data) {
            setSelectedClient(response.data);
            setModalType('client');
            setShowClientModal(true);
            console.log(`✅ [TableauBord] Informations client récupérées:`, response.data);
            return;
          }
        }
      }
      
      // Si rien n'est trouvé
      console.warn(`⚠️ [TableauBord] Contact "${contactNom}" non trouvé`);
      showWarning('Contact introuvable', `Le contact "${contactNom}" n'a pas été trouvé`);
      
    } catch (error) {
      console.error('❌ [TableauBord] Erreur récupération contact:', error);
      showError('Erreur de récupération', 'Erreur lors de la récupération des informations du contact');
    } finally {
      setLoadingClient(false);
    }
  };

  // Fonction pour fermer le modal client/lead
  const fermerModalClient = () => {
    setShowClientModal(false);
    setSelectedClient(null);
    setSelectedLead(null);
    setModalType('client');
  };
  
  // Composant pour les compteurs principaux
  const StatCard = ({ icon, title, value, subvalue, evolution, onClick, className = '' }) => {
    const getEvolutionClass = () => {
      if (typeof evolution === 'string') { // S'assurer que evolution est une chaîne
        if (evolution.includes('+')) return 'positive';
        if (evolution.includes('-')) return 'negative';
      }
      return 'neutral'; // Retourner neutral si evolution n'est pas une chaîne ou ne contient pas +/-
    };
    
    const getEvolutionSymbol = () => {
      if (typeof evolution === 'string') { // S'assurer que evolution est une chaîne
        if (evolution.includes('+')) return '▲';
        if (evolution.includes('-')) return '▼';
      }
      return '◆'; // Retourner ◆ si evolution n'est pas une chaîne ou ne contient pas +/-
    };
    
    return (
      <div className={`stat-card ${onClick ? 'clickable' : ''} ${className}`} onClick={onClick}>
        <div className="stat-card-header"> {/* Nouveau conteneur pour icône et titre de la carte */}
          <div className={`stat-icon ${icon}-icon`}>{icon}</div>
          <div className="stat-info-title"><h4>{title}</h4></div> {/* Conteneur séparé pour le titre de la carte */}
        </div>
        <div className="stat-info-details"> {/* Conteneur pour la valeur, sous-valeur et évolution */}
          <p className="stat-value">{value}</p>
          {subvalue && <p className="stat-subvalue">{subvalue}</p>}
          {evolution && (
            <p className={`stat-evolution ${getEvolutionClass()}`}>
              {evolution} <span>{getEvolutionSymbol()}</span>
            </p>
          )}
        </div>
      </div>
    );
  };  // Rendu du tableau de bord pour les directeurs et administrateurs
  const renderTableauBordDirecteur = () => {
    const leadsVueGlobale = vueGlobale.leads || EMPTY_VUE_GLOBALE.leads;
    const dossiersVueGlobale = vueGlobale.dossiers || EMPTY_VUE_GLOBALE.dossiers;
    const rendezVousVueGlobale = vueGlobale.rendezVous || EMPTY_VUE_GLOBALE.rendezVous;
    const facturationVueGlobale = vueGlobale.facturation || EMPTY_VUE_GLOBALE.facturation;
    const facturesParStatut = facturationVueGlobale.statuts || EMPTY_VUE_GLOBALE.facturation.statuts;
    const comparaisonVueGlobale = vueGlobale.comparaison || EMPTY_VUE_GLOBALE.comparaison;
    const relancesVueGlobale = vueGlobale.relances || EMPTY_VUE_GLOBALE.relances;
    const afficherEvolution = (valeur) => {
      if (valeur === null || valeur === undefined) return '—';
      const nombre = Number(valeur) || 0;
      return `${nombre > 0 ? '+' : ''}${nombre.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`;
    };

    return (
      <>
        <div className="stats-grid">
          {/* Compteurs principaux cliquables */}
          <div className="stats-section compteurs-principaux">
            <h3>Vue d'ensemble</h3>
            <div className="stats-cards">
              <StatCard 
                icon="👥" 
                title="Total Clients" 
                value={stats.totalClients} 
                evolution={`${stats.evolutionClients >= 0 ? '+' : ''}${stats.evolutionClients}%`}
                onClick={() => naviguerVers('clients')}
                className="clickable"
              />
              <StatCard 
                icon="👤" 
                title="Total Leads" 
                value={stats.totalLeads} 
                evolution={`${stats.evolutionLeads >= 0 ? '+' : ''}${stats.evolutionLeads}%`}
                onClick={() => naviguerVers('leads')}
                className="clickable"
              />
              <StatCard 
                icon="💰" 
                title="Factures Impayées" 
                value={stats.facturesEnAttente} 
                subvalue={formaterMontant(stats.montantFacturesEnAttente)}
                evolution={`+${stats.facturesEnAttente > 0 ? '15%' : '0%'}`}
                onClick={() => naviguerVers('facturation')}
                className="clickable"
              />
            </div>
          </div>
          
          {/* Ventes par membre de l'équipe */}
          <div className="stats-section ventes-conseilleres">
            <div className="section-header">
              <h3>Ventes par Responsable</h3>
              <p>Les historiques et les consultations confirmées sont séparés et filtrés sur la période choisie. Ils ne s’additionnent pas.</p>
              {loadingVentes && <span className="loading-indicator">🔄 Chargement ventes...</span>}
              {(userRole === 'administrateur' || userRole === 'admin') && loadingConsultationsConseilleres && 
                <span className="loading-indicator">🔄 Chargement consultations...</span>
              }
            </div>
            <div className="stats-cards">
              {listeComplete.length > 0 ? (
                // Inclut les conseillères et les secrétaires actives, même si
                // aucune facture n'a été créée pendant la période.
                listeComplete.map((item, index) => (
                  <StatCard 
                    key={index}
                    icon="💵" 
                    title={
                      (userRole === 'administrateur' || userRole === 'admin') 
                        ? <><span className="sales-person-role">{item.role === 'secretaire' ? 'Secrétaire' : 'Conseillère'}</span><span className="sales-person-name">{item.conseillere}</span></>
                        : item.conseillere
                    }
                    value={`${item.valeur.toFixed(2)} $ TTC`}
                    subvalue={<>
                      <span className="sales-volume">{item.nombreFactures} facture(s) · {item.totalLeads || 0} lead(s) attribué(s)</span>
                      <span className="sales-history-label">Historique — sources séparées</span>
                      <span className="sales-history-line"><span>Leads marqués consultés</span><strong>{consultationHistoryForName(consultationsParConseillere, item.conseillere).leads}</strong></span>
                      <span className="sales-history-line"><span>RDV terminés attribués</span><strong>{consultationHistoryForName(consultationsParConseillere, item.conseillere).appointments}</strong></span>
                      <span className="sales-history-line sales-history-confirmed"><span>Consultations confirmées</span><strong>{consultationHistoryForName(consultationsParConseillere, item.conseillere).confirmed}</strong></span>
                      {consultationHistoryForName(consultationsParConseillere, item.conseillere).ambiguous && <><br />Nom partagé : consulter le tableau par identifiant.</>}
                    </>}
                    evolution={`${item.evolution >= 0 ? '+' : ''}${item.evolution}%`}
                    onClick={() => naviguerVers('facturation')}
                  />
                ))
              ) : (
                // Afficher les données fictives si pas de vraies données
                stats.ventesParConseillere.map((item, index) => (
                  <StatCard 
                    key={index}
                    icon="💵" 
                    title={
                      (userRole === 'administrateur' || userRole === 'admin') 
                        ? `${item.conseillere} — historique dans le tableau détaillé`
                        : item.conseillere
                    }
                    value={formaterMontant(item.valeur)} 
                    evolution="+7%" 
                    onClick={() => naviguerVers('facturation')}
                  />
                ))
              )}
              <StatCard 
                icon="💵" 
                title="Total Bureau" 
                value={vraiesVentesConseilleres.length > 0 ? 
                  `${vraiesVentesConseilleres.reduce((sum, v) => sum + v.valeur, 0).toFixed(2)} $ TTC` : 
                  formaterMontant(stats.montantFacturesPaye)
                }
                subvalue={vraiesVentesConseilleres.length > 0 ? 
                  `${vraiesVentesConseilleres.reduce((sum, v) => sum + v.nombreFactures, 0)} facture(s)` : 
                  null
                }
                evolution="+7%" 
                onClick={() => naviguerVers('facturation')}
                className="total-bureau"
              />
            </div>
          </div>
        </div>

        <section className="dashboard-overview-section">
          <div className="dashboard-overview-header">
            <div>
              <h3>Indicateurs de pilotage</h3>
              <p>Vue consolidée de l’entreprise pour la période sélectionnée : {titrePage.toLowerCase()}.</p>
            </div>
            {loadingVueGlobale && <span className="dashboard-overview-loading">🔄 Actualisation…</span>}
          </div>

          <div className="dashboard-overview-summary">
            <article className="dashboard-overview-summary-card dashboard-overview-leads">
              <span>Leads reçus</span>
              <strong>{leadsVueGlobale.total}</strong>
              <small>{formaterPourcentage(leadsVueGlobale.tauxConversion)} convertis en clients</small>
            </article>
            <article className="dashboard-overview-summary-card dashboard-overview-contacts">
              <span>Suivi commercial</span>
              <strong>{leadsVueGlobale.contactes + leadsVueGlobale.rendezVousPris}</strong>
              <small>{leadsVueGlobale.contactes} contacté(s) · {leadsVueGlobale.rendezVousPris} rendez-vous pris</small>
            </article>
            <article className="dashboard-overview-summary-card dashboard-overview-dossiers">
              <span>Dossiers créés</span>
              <strong>{dossiersVueGlobale.nouveaux}</strong>
              <small>{dossiersVueGlobale.enCours} en cours · {dossiersVueGlobale.enAttente} en attente / suspendus</small>
            </article>
            <article className="dashboard-overview-summary-card dashboard-overview-rdv">
              <span>Rendez-vous</span>
              <strong>{rendezVousVueGlobale.total}</strong>
              <small>{rendezVousVueGlobale.aVenir} à venir · {rendezVousVueGlobale.realises} réalisés</small>
            </article>
            <article className="dashboard-overview-summary-card dashboard-overview-finance">
              <span>Montant encaissé</span>
              <strong>{formaterMontantsParDevise(facturationVueGlobale.encaisse)}</strong>
              <small>{facturationVueGlobale.total} facture(s) sur la période</small>
            </article>
          </div>

          <div className="dashboard-overview-grid">
            <article className="dashboard-overview-panel">
              <h4>Pipeline des leads</h4>
              <div className="dashboard-overview-metrics">
                <span><b>{leadsVueGlobale.nouveaux}</b>Nouveaux</span>
                <span><b>{leadsVueGlobale.aTraiter}</b>À traiter</span>
                <span><b>{leadsVueGlobale.contactes}</b>Contactés</span>
                <span><b>{leadsVueGlobale.rendezVousPris}</b>Rendez-vous</span>
                <span><b>{leadsVueGlobale.convertis}</b>Convertis</span>
                <span><b>{leadsVueGlobale.perdus}</b>Perdus</span>
              </div>
              {leadsVueGlobale.statuts?.length > 0 && (
                <div className="dashboard-overview-status-list">
                  {leadsVueGlobale.statuts.map((statut) => (
                    <span key={statut.statut}>
                      {statut.statut} <b>{statut.nombre} · {formaterPourcentage(statut.pourcentage)}</b>
                    </span>
                  ))}
                </div>
              )}
            </article>

            <article className="dashboard-overview-panel">
              <h4>Sources des leads</h4>
              {leadsVueGlobale.sources?.length > 0 ? (
                <div className="dashboard-overview-source-list">
                  {leadsVueGlobale.sources.map((source) => (
                    <div key={source.source}>
                      <span>{source.source}</span>
                      <b>{source.nombre} lead{source.nombre > 1 ? 's' : ''}</b>
                      <small>{formaterPourcentage(source.tauxConversion)} convertis</small>
                    </div>
                  ))}
                </div>
              ) : <p className="dashboard-overview-empty">Aucun lead sur cette période.</p>}
            </article>

            <article className="dashboard-overview-panel">
              <h4>Rendez-vous</h4>
              <div className="dashboard-overview-metrics dashboard-overview-metrics-compact">
                <span><b>{rendezVousVueGlobale.aVenir}</b>À venir</span>
                <span><b>{rendezVousVueGlobale.realises}</b>Réalisés</span>
                <span><b>{rendezVousVueGlobale.annules}</b>Annulés</span>
                <span><b>{rendezVousVueGlobale.absents}</b>Absents</span>
              </div>
              <p className="dashboard-overview-note">
                Taux de présence : <b>{rendezVousVueGlobale.tauxPresence === null ? '—' : formaterPourcentage(rendezVousVueGlobale.tauxPresence)}</b>
              </p>
            </article>

            <article className="dashboard-overview-panel">
              <h4>Facturation</h4>
              <div className="dashboard-overview-financial-line">
                <span>À encaisser</span>
                <b>{formaterMontantsParDevise(facturationVueGlobale.aEncaisser)}</b>
              </div>
              <div className="dashboard-overview-invoice-statuses">
                {[
                  ['brouillon', 'Brouillons'],
                  ['payable', 'À payer'],
                  ['partiellement_payee', 'Partiellement payées'],
                  ['payee', 'Payées'],
                  ['en_retard', 'En retard']
                ].map(([statut, libelle]) => (
                  <span key={statut}>
                    {libelle} <b>{facturesParStatut[statut]?.nombre || 0}</b>
                  </span>
                ))}
              </div>
            </article>
          </div>
        </section>

        <section className="dashboard-performance-section">
          <div className="dashboard-performance-header">
            <div>
              <h3>Performance et priorités</h3>
              <p>Comparaison avec la période précédente et suivi des leads qui nécessitent une relance.</p>
            </div>
          </div>

          <div className="dashboard-performance-grid">
            <article className="dashboard-performance-card">
              <span>Leads reçus</span>
              <strong>{leadsVueGlobale.total}</strong>
              <small>
                {comparaisonVueGlobale.leads?.precedent || 0} sur la période précédente ·{' '}
                <b>{afficherEvolution(comparaisonVueGlobale.leads?.evolution)}</b>
              </small>
            </article>
            <article className="dashboard-performance-card">
              <span>Conversions en clients</span>
              <strong>{leadsVueGlobale.convertis}</strong>
              <small>
                {comparaisonVueGlobale.conversions?.precedent || 0} précédemment ·{' '}
                <b>{afficherEvolution(comparaisonVueGlobale.conversions?.evolution)}</b>
              </small>
            </article>
            <article className="dashboard-performance-card dashboard-performance-revenue">
              <span>Revenus encaissés</span>
              {comparaisonVueGlobale.revenus?.length > 0 ? (
                comparaisonVueGlobale.revenus.map((revenu) => (
                  <small key={revenu.monnaie}>
                    <b>{new Intl.NumberFormat('fr-MA', { style: 'currency', currency: revenu.monnaie }).format(revenu.actuel || 0)}</b>
                    {' '}· précédent : {new Intl.NumberFormat('fr-MA', { style: 'currency', currency: revenu.monnaie }).format(revenu.precedent || 0)}
                    {' '}· <b>{afficherEvolution(revenu.evolution)}</b>
                  </small>
                ))
              ) : <strong>—</strong>}
            </article>
            <article className="dashboard-performance-card dashboard-performance-follow-up">
              <span>Relances à traiter</span>
              <strong>{relancesVueGlobale.total || 0}</strong>
              <small>
                {relancesVueGlobale.sansActivite3Jours || 0} sans activité depuis 3 jours ·{' '}
                {relancesVueGlobale.sansActivite7Jours || 0} depuis 7 jours
              </small>
              <div className="dashboard-performance-tags">
                <span>Nouveaux : {relancesVueGlobale.nouveaux || 0}</span>
                <span>À recontacter : {relancesVueGlobale.aRecontacter || 0}</span>
                <span>Injoignables : {relancesVueGlobale.injoignables || 0}</span>
              </div>
            </article>
          </div>
        </section>

        <section className="lead-status-section">
          <div className="lead-status-header">
            <div>
              <h3>Répartition des leads par responsable</h3>
              <p>Statuts actuels des leads selon leur date de création. « Consultation effectuée » est un statut de lead, pas un nombre de consultations distinctes.</p>
            </div>
            <span className="lead-status-total">
              {statutsLeadsParConseillere.totalLeadsAssignes || 0} leads attribués
            </span>
          </div>

          <div className="lead-status-date-filter" aria-label="Filtre par date de création des leads">
            <span className="lead-status-date-filter-label">Date de création des leads</span>
            <label>
              Du
              <input
                type="date"
                value={filtreStatutsLeads.dateDebut}
                onChange={(event) => setFiltreStatutsLeads((previous) => ({ ...previous, dateDebut: event.target.value }))}
              />
            </label>
            <label>
              Au
              <input
                type="date"
                value={filtreStatutsLeads.dateFin}
                onChange={(event) => setFiltreStatutsLeads((previous) => ({ ...previous, dateFin: event.target.value }))}
              />
            </label>
            <button type="button" className="lead-status-filter-apply" onClick={appliquerFiltreStatutsLeads} disabled={loadingStatutsLeads}>
              Filtrer
            </button>
            {(filtreStatutsLeads.dateDebut || filtreStatutsLeads.dateFin) && (
              <button type="button" className="lead-status-filter-reset" onClick={reinitialiserFiltreStatutsLeads} disabled={loadingStatutsLeads}>
                Réinitialiser
              </button>
            )}
          </div>

          {loadingStatutsLeads ? (
            <div className="lead-status-empty">🔄 Chargement de la répartition…</div>
          ) : statutsLeadsParConseillere.responsables?.length > 0 ? (
            <div className="lead-status-grid">
              {statutsLeadsParConseillere.responsables.map((responsable) => (
                <article className="lead-status-card" key={responsable.id}>
                  <div className="lead-status-card-heading">
                    <div>
                      <h4>{responsable.nom}</h4>
                      <span className={`lead-status-role lead-status-role-${responsable.role}`}>
                        {responsable.role === 'secretaire' ? 'Secrétaire' : 'Conseillère'}
                      </span>
                      {userRole === 'super_admin' && responsable.entreprise && (
                        <span className="lead-status-company">{responsable.entreprise}</span>
                      )}
                    </div>
                    <strong>{responsable.totalLeads} lead{responsable.totalLeads > 1 ? 's' : ''}</strong>
                  </div>

                  <div className="lead-status-performance" aria-label={`Performance de ${responsable.nom}`}>
                    <span>Traités <b>{responsable.performance?.traites || 0}</b></span>
                    <span>À suivre <b>{responsable.performance?.aTraiter || 0}</b></span>
                    <span>Convertis <b>{responsable.performance?.convertis || 0}</b></span>
                    <span>Fermés <b>{responsable.performance?.fermes || 0}</b></span>
                    <span>Conversion <b>{formaterPourcentage(responsable.performance?.tauxConversion)}</b></span>
                  </div>

                  {responsable.statuts?.length > 0 ? (
                    <div className="lead-status-pills">
                      {responsable.statuts.map((statut) => (
                        <span className="lead-status-pill" key={statut.statut}>
                          <span>{statut.statut}</span>
                          <b>{statut.nombre} · {Number(statut.pourcentage || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %</b>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="lead-status-no-assignment">Aucun lead attribué</p>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="lead-status-empty">Aucune conseillère ou secrétaire active à afficher.</div>
          )}
        </section>
        
        {/* Widget Mes Tâches */}
        <MesTaches userRole={userRole} userName={userName} />
        
        <div className="bottom-grid">
          {/* Rendez-vous à venir */}
          <div className="rendez-vous-section">
            <div className="section-header">
              <h3>Rendez-vous à Venir</h3>
              {loadingRendezVous && <span className="loading-indicator">🔄 Chargement...</span>}
            </div>
            <div className="rendez-vous-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Conseillère</th>
                  </tr>
                </thead>
                <tbody>
                  {(vraiesRendezVousAVenir.length > 0 ? vraiesRendezVousAVenir : rendezVousAVenir).map(rdv => (
                    <tr key={rdv.id}>
                      <td>
                        <span 
                          className="client-link" 
                          onClick={() => {
                            // Déterminer le type selon les données disponibles
                            const contactType = rdv.client_type || (rdv.client_id ? 'client' : 'lead');
                            const contactId = rdv.client_id;
                            const contactNom = rdv.client || rdv.client_nom;
                            afficherInfosContact(contactId, contactNom, contactType);
                          }}
                          style={{ cursor: 'pointer', color: '#1a73e8', textDecoration: 'underline' }}
                          title={`Cliquer pour voir les informations du ${rdv.client_type === 'lead' ? 'lead' : 'client'}`}
                        >
                          {loadingClient ? '🔄 Chargement...' : (
                            <span>
                              {rdv.client_type === 'lead' ? '🎯 ' : '👤 '}{rdv.client || rdv.client_nom}
                            </span>
                          )}
                        </span>
                      </td>
                      <td>{rdv.date || rdv.date_rdv}</td>
                      <td>{rdv.type || rdv.type_rdv}</td>
                      <td>{rdv.conseillere || rdv.conseillere_nom}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(vraiesRendezVousAVenir.length === 0 && rendezVousAVenir.length === 0) && !loadingRendezVous && (
                <div className="no-data">
                  <p>Aucun rendez-vous à venir</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Activités récentes */}
          <div className="activites-section">
            <div className="section-header">
              <h3>Activités Récentes</h3>
              {loadingActivites && <span className="loading-indicator">🔄 Chargement...</span>}
              {derniereMAJActivites && !loadingActivites && (
                <span className="last-update" title="Dernière mise à jour">
                  🕒 {new Date(derniereMAJActivites).toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              )}
            </div>
            <div className="activites-list">
              {(vraiesActivitesRecentes.length > 0 ? vraiesActivitesRecentes : activitesRecentes)
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map(activite => (
                <div key={activite.id} className="activite-item">
                  <div className="activite-icon">{getIconeActivite(activite.type)}</div>
                  <div className="activite-content">
                    <p className="activite-description">{activite.description}</p>
                    <div className="activite-meta">
                      <span className="activite-user">{activite.utilisateur}</span>
                      <span className="activite-date">{activite.date}</span>
                    </div>
                  </div>
                </div>
              ))}
              {(vraiesActivitesRecentes.length === 0 && activitesRecentes.length === 0) && !loadingActivites && (
                <div className="no-data">
                  <p>Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Graphiques et statistiques supplémentaires */}
        <div className="charts-grid">
          <div className="chart-section">
            <h3>Leads par Source</h3>
            <div className="filter-buttons">
              <button className={periode === 'jour' ? 'active' : ''} onClick={() => changerPeriode('jour')}>Jour</button>
              <button className={periode === 'semaine' ? 'active' : ''} onClick={() => changerPeriode('semaine')}>Semaine</button>
              <button className={periode === 'mois' ? 'active' : ''} onClick={() => changerPeriode('mois')}>Mois</button>
              <button className={periode === 'annee' ? 'active' : ''} onClick={() => changerPeriode('annee')}>Année</button>
            </div>
            {renderBarChart(graphData.leadsParSource)}
          </div>
          
          <div className="chart-section">
            <h3>Revenus par Mois</h3>
            <div className="filter-buttons">
              <button className={periode === 'jour' ? 'active' : ''} onClick={() => changerPeriode('jour')}>Jour</button>
              <button className={periode === 'semaine' ? 'active' : ''} onClick={() => changerPeriode('semaine')}>Semaine</button>
              <button className={periode === 'mois' ? 'active' : ''} onClick={() => changerPeriode('mois')}>Mois</button>
              <button className={periode === 'annee' ? 'active' : ''} onClick={() => changerPeriode('annee')}>Année</button>
            </div>
            {renderBarChart(graphData.revenusParMois)}
          </div>
          
          <div className="chart-section">
            <h3>Dossiers par Type</h3>
            <div className="filter-buttons">
              <button className={periode === 'jour' ? 'active' : ''} onClick={() => changerPeriode('jour')}>Jour</button>
              <button className={periode === 'semaine' ? 'active' : ''} onClick={() => changerPeriode('semaine')}>Semaine</button>
              <button className={periode === 'mois' ? 'active' : ''} onClick={() => changerPeriode('mois')}>Mois</button>
              <button className={periode === 'annee' ? 'active' : ''} onClick={() => changerPeriode('annee')}>Année</button>
            </div>
            {renderBarChart(graphData.dossiersParType)}
          </div>
        </div>
      </>
    );
  };

  // Rendu du tableau de bord pour les conseillères
  const renderTableauBordConseillere = () => {
    return (
      <>
        <div className="stats-grid">
          {/* Compteurs personnels cliquables */}
          <div className="stats-section">
            <h3>Mes Statistiques</h3>
            <div className="stats-cards">
              <StatCard 
                icon="👥" 
                title="Mes Clients" 
                value={stats.mesClients} 
                evolution="+5%" 
                onClick={() => naviguerVers('clients')}
              />
              <StatCard 
                icon="👤" 
                title="Mes Leads" 
                value={stats.mesLeads} 
                evolution="+3%" 
                onClick={() => naviguerVers('leads')}
              />
              <StatCard 
                icon="💰" 
                title="Factures Impayées" 
                value={stats.mesFacturesImpayees} 
                evolution="+1%" 
                onClick={() => naviguerVers('facturation')}
              />
            </div>
          </div>
          
          {/* Mes ventes réelles */}
          <div className="stats-section">
            <div className="section-header">
              <h3>Mes Ventes</h3>
              {(loadingMesVentes || loadingConsultations) && <span className="loading-indicator">🔄 Chargement...</span>}
            </div>
            <div className="stats-cards">
              <StatCard 
                icon="💰" 
                title="Mon Total TTC" 
                value={`${mesVentesData.monTotalTTC.toFixed(2)} $`}
                subvalue={`${mesVentesData.monNombreFactures} facture(s)`}
                evolution="+12%" 
                onClick={() => naviguerVers('facturation')}
              />
              <StatCard 
                icon="✅" 
                title="Factures Payées" 
                value={mesVentesData.facturesPayees || 0} 
                evolution="+8%" 
                onClick={() => naviguerVers('facturation', { filtreStatut: 'Payée' })}
              />
              <StatCard 
                icon="⏳" 
                title="En Attente" 
                value={mesVentesData.facturesEnAttente || 0} 
                evolution="+5%" 
                onClick={() => naviguerVers('facturation')}
              />
              <StatCard 
                icon="🎯" 
                title="Consultations" 
                value={consultationsData.totalConsultations || 0} 
                evolution={consultationsData.evolution || '+0%'} 
                onClick={() => naviguerVers('leads', { filtreStatut: 'Consultation effectuée' })}
              />
            </div>
          </div>
        </div>
        
        {/* Widget Mes Tâches */}
        <MesTaches userRole={userRole} userName={userName} />
        
        <div className="bottom-grid">
          {/* Mes rendez-vous à venir */}
          <div className="rendez-vous-section">
            <div className="section-header">
              <h3>Mes Rendez-vous à Venir</h3>
              {loadingRendezVous && <span className="loading-indicator">🔄 Chargement...</span>}
            </div>
            <div className="rendez-vous-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Type</th>
                  </tr>
                </thead>
                <tbody>
                  {(vraiesRendezVousAVenir.length > 0 ? vraiesRendezVousAVenir : mesRendezVousAVenir).map(rdv => (
                    <tr key={rdv.id}>
                      <td>
                        <span 
                          className="client-link" 
                          onClick={() => {
                            // Déterminer le type selon les données disponibles
                            const contactType = rdv.client_type || (rdv.client_id ? 'client' : 'lead');
                            const contactId = rdv.client_id;
                            const contactNom = rdv.client || rdv.client_nom;
                            afficherInfosContact(contactId, contactNom, contactType);
                          }}
                          style={{ cursor: 'pointer', color: '#1a73e8', textDecoration: 'underline' }}
                          title={`Cliquer pour voir les informations du ${rdv.client_type === 'lead' ? 'lead' : 'client'}`}
                        >
                          {loadingClient ? '🔄 Chargement...' : (
                            <span>
                              {rdv.client_type === 'lead' ? '🎯 ' : '👤 '}{rdv.client || rdv.client_nom}
                            </span>
                          )}
                        </span>
                      </td>
                      <td>{rdv.date || rdv.date_rdv}</td>
                      <td>{rdv.type || rdv.type_rdv}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(vraiesRendezVousAVenir.length === 0 && mesRendezVousAVenir.length === 0) && !loadingRendezVous && (
                <div className="no-data">
                  <p>Aucun rendez-vous à venir</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Mes activités récentes */}
          <div className="activites-section">
            <div className="section-header">
              <h3>Mes Activités Récentes</h3>
              {loadingActivites && <span className="loading-indicator">🔄 Chargement...</span>}
              {derniereMAJActivites && !loadingActivites && (
                <span className="last-update" title="Dernière mise à jour">
                  🕒 {new Date(derniereMAJActivites).toLocaleTimeString('fr-FR', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </span>
              )}
            </div>
            <div className="activites-list">
              {(vraiesActivitesRecentes.length > 0 ? vraiesActivitesRecentes : mesActivitesRecentes)
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map(activite => (
                <div key={activite.id} className="activite-item">
                  <div className="activite-icon">{getIconeActivite(activite.type)}</div>
                  <div className="activite-content">
                    <p className="activite-description">{activite.description}</p>
                    <div className="activite-meta">
                      <span className="activite-date">{activite.date}</span>
                    </div>
                  </div>
                </div>
              ))}
              {(vraiesActivitesRecentes.length === 0 && mesActivitesRecentes.length === 0) && !loadingActivites && (
                <div className="no-data">
                  <p>Aucune activité récente</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Mes factures */}
        <div className="factures-section">
          <div className="section-header">
            <h3>Mes Factures</h3>
            <div className="filter-buttons">
              <button className={periode === 'jour' ? 'active' : ''} onClick={() => changerPeriode('jour')}>Jour</button>
              <button className={periode === 'semaine' ? 'active' : ''} onClick={() => changerPeriode('semaine')}>Semaine</button>
              <button className={periode === 'mois' ? 'active' : ''} onClick={() => changerPeriode('mois')}>Mois</button>
            </div>
          </div>
          <div className="factures-list">
            {mesVentesData.mesFactures && mesVentesData.mesFactures.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Numéro</th>
                    <th>Client</th>
                    <th>Montant TTC</th>
                    <th>Statut</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {mesVentesData.mesFactures.map(facture => (
                    <tr key={facture.id}>
                      <td>{facture.numero}</td>
                      <td>{facture.client}</td>
                      <td>{facture.montantTTC.toFixed(2)} $</td>
                      <td>
                        <span className={`statut-badge ${facture.statut === 'payee' ? 'statut-success' : 'statut-warning'}`}>
                          {facture.statut === 'payee' ? 'Payée' : 'En attente'}
                        </span>
                      </td>
                      <td>{new Date(facture.dateEmission).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="no-data">
                <p>Aucune facture pour cette période</p>
                {loadingMesVentes && <p>🔄 Chargement en cours...</p>}
              </div>
            )}
          </div>
        </div>
      </>
    );
  };

  // Rendu du tableau de bord pour le comptable
  const renderTableauBordComptable = () => {
    return (
      <>
        <div className="stats-grid">
          {/* Compteurs financiers */}
          <div className="stats-section">
            <h3>Aperçu Financier</h3>
            <div className="stats-cards">
              <StatCard 
                icon="💵" 
                title="Ventes du Mois" 
                value={formaterMontant(stats.totalVentes)} 
                evolution="+12%" 
              />
              <StatCard 
                icon="💰" 
                title="Paiements Récents" 
                value={formaterMontant(stats.paiementsRecents)} 
                evolution="+8%" 
              />
              <StatCard 
                icon="📊" 
                title="Factures en Attente" 
                value={stats.facturesEnAttente} 
                subvalue={formaterMontant(stats.montantFacturesEnAttente)}
                evolution="+15%" 
                onClick={() => naviguerVers('facturation')}
              />
              <StatCard 
                icon="💸" 
                title="Charges à Venir" 
                value={stats.chargesAVenir} 
                subvalue={formaterMontant(stats.montantChargesAVenir)}
                evolution="+5%" 
                onClick={() => naviguerVers('charges')}
              />
            </div>
          </div>
        </div>
        
        <div className="bottom-grid">
          {/* Dernières factures */}
          <div className="factures-section">
            <h3>Dernières Factures</h3>
            <div className="factures-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Client</th>
                    <th>Montant</th>
                    <th>Date</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {dernieresFactures.map(facture => (
                    <tr key={facture.id}>
                      <td>{facture.id}</td>
                      <td>{facture.client}</td>
                      <td>{formaterMontant(facture.montant)}</td>
                      <td>{facture.date}</td>
                      <td>
                        <span className={`statut-badge ${facture.statut === 'Payée' ? 'statut-success' : 'statut-warning'}`}>
                          {facture.statut}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Paiements récents */}
          <div className="paiements-section">
            <h3>Paiements Récents</h3>
            <div className="paiements-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Facture</th>
                    <th>Client</th>
                    <th>Montant</th>
                    <th>Date</th>
                    <th>Méthode</th>
                  </tr>
                </thead>
                <tbody>
                  {paiementsRecents.map(paiement => (
                    <tr key={paiement.id}>
                      <td>{paiement.id}</td>
                      <td>{paiement.facture}</td>
                      <td>{paiement.client}</td>
                      <td>{formaterMontant(paiement.montant)}</td>
                      <td>{paiement.date}</td>
                      <td>{paiement.methode}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Charges */}
        <div className="charges-section">
          <h3>Charges</h3>
          <div className="charges-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Description</th>
                  <th>Montant</th>
                  <th>Date</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {charges.map(charge => (
                  <tr key={charge.id}>
                    <td>{charge.id}</td>
                    <td>{charge.description}</td>
                    <td>{formaterMontant(charge.montant)}</td>
                    <td>{charge.date}</td>
                    <td>
                      <span className={`statut-badge ${charge.statut === 'Payée' ? 'statut-success' : 'statut-warning'}`}>
                        {charge.statut}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </>
    );
  };

  // Rendu du tableau de bord pour la secrétaire
  const renderTableauBordSecretaire = () => {
    return (
      <>
        <div className="stats-grid">
          {/* Compteurs pour la secrétaire */}
          <div className="stats-section">
            <h3>Aperçu du Jour</h3>
            <div className="stats-cards">
              <StatCard 
                icon="📅" 
                title="Rendez-vous Aujourd'hui" 
                value={stats.rendezVousJour} 
                evolution="+2%" 
                onClick={() => naviguerVers('rendez-vous')}
              />
              <StatCard 
                icon="👤" 
                title="Nouveaux Leads" 
                value={stats.nouveauxLeads} 
                evolution="+5%" 
                onClick={() => naviguerVers('leads')}
              />
              <StatCard 
                icon="📋" 
                title="Leads à Traiter" 
                value={stats.leadsATraiter} 
                evolution="+3%" 
                onClick={() => naviguerVers('leads')}
              />
              <StatCard 
                icon="💬" 
                title="Consultations" 
                value={consultationsData.totalConsultations || 0} 
                evolution={consultationsData.evolution || '+0%'} 
                onClick={() => naviguerVers('consultations')}
              />
            </div>
          </div>
        </div>
        
        <div className="bottom-grid">
          {/* Widget Mes Tâches pour la secrétaire */}
          <div className="mes-taches-section">
            <MesTaches userRole={userRole} userName={userName} />
          </div>
          
          {/* Rendez-vous du jour */}
          <div className="rendez-vous-section">
            <h3>Rendez-vous du Jour</h3>
            <div className="rendez-vous-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Heure</th>
                    <th>Type</th>
                    <th>Conseillère</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {rendezVousJour.map(rdv => (
                    <tr key={rdv.id}>
                      <td>
                        {(rdv.lead_id || rdv.client_id) ? (
                          <button 
                            onClick={() => {
                              if (rdv.lead_id) {
                                console.log('🔄 Navigation vers Lead ID:', rdv.lead_id);
                                localStorage.setItem('selectedLeadId', rdv.lead_id);
                                onNavigate('leads');
                              } else if (rdv.client_id) {
                                console.log('🔄 Navigation vers Client ID:', rdv.client_id);
                                localStorage.setItem('selectedClientId', rdv.client_id);
                                onNavigate('clients');
                              }
                            }}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#007bff',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                              font: 'inherit'
                            }}
                            title={rdv.lead_id ? "Voir le lead" : "Voir le client"}
                          >
                            {rdv.client}
                          </button>
                        ) : (
                          rdv.client
                        )}
                      </td>
                      <td>{rdv.heure}</td>
                      <td>{rdv.type}</td>
                      <td>{rdv.conseillere}</td>
                      <td>
                        <span className={`statut-badge ${rdv.statut === 'Terminé' ? 'statut-success' : rdv.statut === 'En cours' ? 'statut-info' : 'statut-warning'}`}>
                          {rdv.statut}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Nouveaux leads */}
          <div className="leads-section">
            <h3>Nouveaux Leads</h3>
            <div className="leads-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nom</th>
                    <th>Email</th>
                    <th>Téléphone</th>
                    <th>Source</th>
                    <th>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {nouveauxLeads.map(lead => (
                    <tr key={lead.id}>
                      <td>{lead.nom}</td>
                      <td>{lead.email}</td>
                      <td>{lead.telephone}</td>
                      <td>{lead.source}</td>
                      <td>{lead.date}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          {/* Consultations récentes */}
          <div className="consultations-section">
            <div className="section-header">
              <h3>Consultations Récentes</h3>
              {loadingConsultations && <span className="loading-indicator">🔄 Chargement...</span>}
            </div>
            <div className="consultations-list">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Date</th>
                    <th>Heure</th>
                    <th>Conseillère</th>
                    <th>Type</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {consultationsData.consultationsRecentes && consultationsData.consultationsRecentes.length > 0 ? (
                    consultationsData.consultationsRecentes.map(consultation => (
                      <tr key={consultation.id}>
                        <td>
                          <span 
                            className="client-link" 
                            onClick={() => {
                              const contactType = consultation.client_type || (consultation.client_id ? 'client' : 'lead');
                              const contactId = consultation.client_id;
                              const contactNom = consultation.client_nom || consultation.client;
                              afficherInfosContact(contactId, contactNom, contactType);
                            }}
                            style={{ cursor: 'pointer', color: '#1a73e8', textDecoration: 'underline' }}
                            title={`Cliquer pour voir les informations du ${consultation.client_type === 'lead' ? 'lead' : 'client'}`}
                          >
                            {loadingClient ? '🔄 Chargement...' : (
                              <span>
                                {consultation.client_type === 'lead' ? '🎯 ' : '👤 '}{consultation.client_nom || consultation.client}
                              </span>
                            )}
                          </span>
                        </td>
                        <td>{consultation.date_consultation || consultation.date}</td>
                        <td>{consultation.heure_consultation || consultation.heure}</td>
                        <td>{consultation.conseillere_nom || consultation.conseillere}</td>
                        <td>{consultation.type_consultation || consultation.type || 'Consultation'}</td>
                        <td>
                          <span className={`statut-badge ${
                            consultation.statut === 'Terminée' ? 'statut-success' : 
                            consultation.statut === 'En cours' ? 'statut-info' : 
                            'statut-warning'
                          }`}>
                            {consultation.statut || 'Planifiée'}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-data">
                        {loadingConsultations ? 'Chargement des consultations...' : 'Aucune consultation récente'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </>
    );
  };

  // Rendu principal du tableau de bord selon le rôle
  return (
    <div className="tableau-bord-container">
      {/* En-tête avec titre et sélection de période */}
      <div className="header-container">
        <div className="title-container">
          <h1>{titrePage}</h1> {/* Utilisation de l'état titrePage pour un titre dynamique */}
          <span className="vue-globale-badge" title="Les statistiques affichent les données de tous les utilisateurs">
            🌍 Vue Globale
          </span>
        </div>
        <div className="header-actions">
          <div className="periode-selector">
          <button 
            className={`btn-periode ${periode === 'jour' ? 'active' : ''}`}
            onClick={() => changerPeriode('jour')}
          >
            Jour
          </button>
          <button 
            className={`btn-periode ${periode === 'semaine' ? 'active' : ''}`}
            onClick={() => changerPeriode('semaine')}
          >
            Semaine
          </button>
          <button 
            className={`btn-periode ${periode === 'mois' ? 'active' : ''}`}
            onClick={() => changerPeriode('mois')}
          >
            Mois
          </button>
          <button 
            className={`btn-periode ${periode === 'trimestre' ? 'active' : ''}`}
            onClick={() => changerPeriode('trimestre')}
          >
            Trimestre
          </button>
          <button 
            className={`btn-periode ${periode === 'annee' ? 'active' : ''}`}
            onClick={() => changerPeriode('annee')}
          >
            Année
          </button>
          </div>
          <button
            type="button"
            className="btn-export-dashboard"
            onClick={exporterTableauDeBord}
            title="Télécharger les statistiques visibles du tableau de bord"
          >
            ⬇ Exporter
          </button>
        </div>
      </div>
      
      {userRole === 'administrateur' && renderTableauBordDirecteur()}
      {userRole === 'super_admin' && renderTableauBordDirecteur()}
      {userRole === 'admin' && renderTableauBordDirecteur()}
      {userRole === 'manager' && renderTableauBordDirecteur()}
      {userRole === 'directeur' && renderTableauBordDirecteur()}
      {userRole === 'conseillere' && renderTableauBordConseillere()}
      {userRole === 'comptable' && renderTableauBordComptable()}
      {userRole === 'secretaire' && renderTableauBordSecretaire()}
      {['super_admin', 'admin', 'administrateur', 'manager', 'directeur', 'conseillere', 'secretaire'].includes(userRole) && <ConsultationAnalytics period={periode} userRole={userRole} onData={setConsultationExportData} onChanged={() => { if (DASHBOARD_RESPONSIBLE_ROLES.includes(userRole)) chargerConsultationsConseilleres(periode); else chargerConsultations(periode); }} />}

      {/* Modal pour afficher les informations du client ou lead */}
      {showClientModal && (selectedClient || selectedLead) && (
        <ClientDetailModal
          client={modalType === 'client' ? selectedClient : null}
          lead={modalType === 'lead' ? selectedLead : null}
          modalType={modalType}
          isOpen={showClientModal}
          onClose={fermerModalClient}
          readOnly={true}
          showAll={true}
        />
      )}
    </div>
  );
}

export default TableauBord;
